/*
 * Copyright (c) 2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { acceptAll } from "../../../../provisioning/ProvisioningUtil";
import { validateFileSize } from "../../../../util/File";
import EmailEditor from 'react-email-editor';
import FormInput from "../../../../provisioning/elements/FormInput";
import DismissablePopover from "../../../../elements/DismissablePopover";
import Media from "../../../../elements/Media";
import ActionButton from "../../../../elements/ActionButton";
import useUpdateState from "../../../../util/useUpdateState";
import { region } from "../../../../util/constants/Endpoint";

export const TagPopover = ({ isOpen, toggle, target, onTag }) => {
  // constants
  const tagList = [
    { icon: "person", title: "Name", tag: "{{ name }}" },
    { icon: "envelope", title: "Email", tag: "{{ email }}" },
    { icon: "telephone", title: "Phone Number", tag: "{{ phone_number }}" },
    { icon: "globe2", title: "URL", tag: "url" },
  ];

  // state
  const initialState = { url: null, invalidUrl: false };
  const [state,, updateState] = useUpdateState({ ...initialState });

  // reset view if opened
  useEffect(() => {
    if (isOpen) {
      updateState({ ...initialState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // handlers
  const onTagSelect = (tag) => {
    switch (tag) {
      case "url": updateState({ url: "" }); break;
      default: onTag?.(tag); break;
    }
  };

  const onUrlConfirm = () => {
    let urlTag = state.url;

    // add https:// if protocol not included in url
    const hasProtocolRegex = /^(https:\/\/|http:\/\/)/;
    if (!hasProtocolRegex.test(urlTag)) {
      urlTag = "https://" + urlTag;
    }

    // validate url
    const validUrlRegex = /^(https|http):\/\/([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    if (!validUrlRegex.test(urlTag)) {
      updateState({ invalidUrl: true });
    } 
    
    // return tagged url
    else {
      onTag?.(`{{ ${urlTag} }}`);
    }
  };

  return (
    <DismissablePopover placement="top" isOpen={isOpen} target={target} toggle={toggle}>
      <div className="m-2">
        {state.url != null ? (
          <>
            <Label className="no-spacing" size="sm">Enter URL</Label>
            <div className="d-flex">
              <Input
                className="flex-dynamic"
                bsSize="sm"
                value={state.url}
                placeholder="https://example.com"
                onChange={(e) => updateState({ url: e.target.value })}
                invalid={state.invalidUrl}
              />
              <ActionButton className="ms-1" type="confirm" onClick={onUrlConfirm} />
            </div>
          </>
        ) : tagList.map((item, index) => (
          <div
            key={index}
            className="d-flex hoverable-item"
            onClick={() => onTagSelect(item.tag)}
            style={{ fontSize: "0.8rem" }}
          >
            <i className={`icon bi bi-${item.icon}`} />
            <span className="px-1">{item.title}</span>
          </div>
        ))}
      </div>
    </DismissablePopover>
  );
};

export const smsMaxChars = 175;

export const verifySmsMessage = (sms) => {
  if (sms === null || sms === undefined) { 
    // Assume empty
    return false;
  }

  const length = sms.length;
  return length > 0 && length <= smsMaxChars;
}

const MessageEditorStep = ({name, form, emailEditorRef, setNextEnabled}) => {
  // tag popover state
  const [ popover, setPopover ] = useState(false);

  // users
  const { watch, setValue } = form;
  const types = watch("type");
  const type = Object.keys(types).find(key => types[key] === true);

  // input props
  const inputProps = { ...form, path: name };

  const smsBodyId = `${name}.smsBody`;
  const smsBody = watch(smsBodyId);
  const initialMediaId = `${name}.smsPreviousMedia`;
  const initialMedia = watch(initialMediaId);
  const [smsBodySelection, setSmsBodySelection] = useState({ start: null, end: null});

  // constants

  // handlers
  const insertTag = (tag) => {
    let newValue;
    if (smsBodySelection.start !== null && smsBodySelection.end !== null) {
      const startPos = smsBodySelection.start;
      const endPos = smsBodySelection.end;
      newValue = smsBody.substring(0, startPos) + tag + smsBody.substring(endPos, smsBody.length);
    } else {
      newValue = smsBody + tag;
    }

    if (Array.from(newValue).length <= smsMaxChars) {
      setValue(smsBodyId, newValue);
    }
    setPopover(false);
  };

  const handleSmsBodyBlur = (event) => {
    const startPos = event.target.selectionStart;
    const endPos = event.target.selectionEnd;

    setSmsBodySelection({ start: startPos, end: endPos });
  }

  const validateMediaFileSize = (fileList) => {
    const type = fileList?.[0]?.type;
    if (type === null) {
      return;
    }

    if (/image/.test(type)) {
      return validateFileSize(fileList, 1048576) ? true : "Images cannot be larger than 1MB";
    } else {
      return validateFileSize(fileList, 614400) ? true : "Audio/video files cannot be larger than 600KB";
    }
  };

  const validateSmsBody = (event) => {
    // Do nothing
    if (type === "sms") {
      setNextEnabled(verifySmsMessage(event.target.value));
    }
  };

  const showMms = region !== "EU";

  return (
    <div className="force-fill-container d-flex flex-column align-items-center">
      <div className="d-flex justify-content-between">
        <Label className="pe-3 flex-static text-center" tag="h2">
          Draft Message
        </Label>
      </div>
      {type === "email" && (
        <div className="flex-dynamic fill-container d-flex">
          {/* <Button onClick={() => {
            emailEditorRef.current.editor.exportHtml((data) => {
              console.log(data);
            })
          }}>
            Log Design
          </Button> */}
          <EmailEditor
            ref={emailEditorRef}
            style={{ padding: "10px 0 10px 0", height: "95%" }}
            onLoad={(unlayer) => {
              // add tags option for text
              unlayer.setMergeTags({
                name: {
                  name: "Name",
                  value: "{{ name }}",
                  sample: "{{ name }}",
                },
                email: {
                  name: "Email",
                  value: "{{ email }}",
                  sample: "{{ email }}",
                },
                phoneNumber: {
                  name: "Phone Number",
                  value: "{{ phone_number }}",
                  sample: "{{ phone_number }}",
                }
              });
            }}
            // onReady={onReady}
            />
        </div>
      )}
      {type === "sms" && (
        <div className="flex-static" style={{ width: "90%" }}>
          <Label className="fw-bold">Message Body</Label>
          <div className="d-flex">
            <span>Insert tag</span>
            <Button
              id="tag-button"
              outline
              size="sm"
              onClick={() => setPopover(true)}
              style={{ margin: "0 0 3px 3px", padding: "3px 2px 0 2px" }}
            >
              <i className="icon bi bi-caret-up-fill" />
            </Button>
            <TagPopover
              isOpen={popover}
              toggle={() => setPopover(prev => !prev)}
              target="tag-button"
              onTag={insertTag}
            />
          </div>
          <FormInput
            {...inputProps}
            type="textarea"
            name="smsBody"
            placeholder={`Enter your message here (max ${smsMaxChars} chars)`}
            required
            reqInd={false}
            maxChars={smsMaxChars}
            onChange={validateSmsBody}
            onBlur={handleSmsBodyBlur}
          />
          <div>
            <Label className="d-flex m-2 justify-content-end">{smsMaxChars - (smsBody?.length ?? 0)} / {smsMaxChars} remaining</Label>
          </div>
          <br />
          {showMms && 
            <div>
              <Label className="fw-bold">Upload media file (optional)</Label>
              {initialMedia != null ? (
                <div>
                  <Label className="no-spacing">
                    Message is using previously uploaded file
                    <span className="fw-bold"> {initialMedia.name}</span>:
                  </Label>
                  <br />
                  <Media src={initialMedia.url} style={{ maxHeight: "300px" }} />
                  <br /><br />
                  <Label>Would you like to delete this media?</Label>
                  <br />
                  <Button outline color="info" onClick={() => setValue(initialMediaId, null)}>Delete Media</Button>
                </div>
              ) : (
                <>
                  <FormInput
                    {...inputProps}
                    type="file"
                    accept={acceptAll}
                    validate={validateMediaFileSize}
                    name="smsMedia"
                  />
                  <Label className="no-spacing" size="sm">
                    Supports image files (.jpg, .png, .gif) up to 1MB and audio/video files (.mp3, .mp4) up to 600KB
                  </Label>
                </>
              )}
            </div>
          }
          
        </div>
      )}
    </div>
  );
};

export default MessageEditorStep;
